import { exportExcel, exportRowsToPdf, type ExportRow } from '../lib/exportReports';
import type { PlatformAnalytics } from '../api';
import type { OrganizationPublic } from '../lib/tenant';

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

export function buildTenantRegistryRows(orgs: OrganizationPublic[]): ExportRow[] {
  return orgs.map((o) => ({
    Organization: o.name,
    TenantId: o.id,
    AdminEmail: o.adminEmail,
    Slug: o.slug,
    Plan: o.planId ?? '—',
    Billing: o.subscriptionStatus,
    AccessHold: o.accountHold ? 'Yes' : 'No',
    HoldReason: o.accountHoldReason ?? '—',
    DataKeys: o.dataKeys ?? 0,
    SmsInPeriod: o.whatsappMessagesInPeriod ?? 0,
    LastActivity: o.lastWorkspaceAt ? new Date(o.lastWorkspaceAt).toLocaleString('en-IN') : '—',
    Registered: new Date(o.createdAt).toLocaleDateString('en-IN'),
  }));
}

export function buildBillingRows(orgs: OrganizationPublic[]): ExportRow[] {
  return orgs.map((o) => ({
    Organization: o.name,
    AdminEmail: o.adminEmail,
    Plan: o.planId ?? '—',
    Status: o.subscriptionStatus,
    OnHold: o.accountHold ? 'Yes' : 'No',
    Registered: new Date(o.createdAt).toLocaleDateString('en-IN'),
  }));
}

export function buildUsageRows(orgs: OrganizationPublic[]): ExportRow[] {
  return [...orgs]
    .sort((a, b) => (b.whatsappMessagesInPeriod ?? 0) - (a.whatsappMessagesInPeriod ?? 0))
    .map((o) => ({
      Organization: o.name,
      AdminEmail: o.adminEmail,
      DataKeys: o.dataKeys ?? 0,
      SmsMessages: o.whatsappMessagesInPeriod ?? 0,
      EstSmsCostInr: ((o.whatsappMessagesInPeriod ?? 0) * 0.25).toFixed(2),
      LastActivity: o.lastWorkspaceAt ? new Date(o.lastWorkspaceAt).toLocaleString('en-IN') : '—',
    }));
}

export function buildPlatformSummaryRows(analytics: PlatformAnalytics, dateLabel: string): ExportRow[] {
  return [
    { Metric: 'Reporting period', Value: dateLabel },
    { Metric: 'Total organizations', Value: analytics.total },
    { Metric: 'Active billing', Value: analytics.activeBilling },
    { Metric: 'Pending checkout', Value: analytics.pending },
    { Metric: 'Past due', Value: analytics.pastDue },
    { Metric: 'Access on hold', Value: analytics.onHold },
    { Metric: 'Signups in period', Value: analytics.signupsInPeriod ?? analytics.signups30d },
    { Metric: 'Active tenants in period', Value: analytics.activeInPeriod ?? analytics.activeLast14d },
    { Metric: 'SMS messages (period)', Value: analytics.smsMessagesTotal ?? analytics.whatsappMessagesTotal },
    { Metric: 'SMS est. cost INR', Value: (analytics.smsApiCostInr ?? analytics.whatsappApiCostInr).toFixed(2) },
    { Metric: 'Total workspace data keys', Value: analytics.totalDataKeys },
    { Metric: 'Generated at', Value: new Date().toLocaleString('en-IN') },
  ];
}

export function exportPlatformPdf(title: string, rows: ExportRow[], dateLabel: string) {
  exportRowsToPdf(`${title} — ${dateLabel}`, rows);
}

export function exportPlatformExcel(filename: string, rows: ExportRow[]) {
  exportExcel(`${filename}-${isoDate(new Date())}`, rows);
}
